import { useEffect, useState, useLayoutEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
    UserSession,
    TeamsList,
    sanitize,
    extension
} from '../../utils'
import { Client, Account, Teams, Functions, Storage } from "appwrite";
import logo from "../../assets/tom-logo-icon-black.jpg"

import Popup from '../../components/Popup'
import {
    createUser,
    getTeams,
    createTeam,
    getWidgetData
} from '../../appwrite/methods'
import DashboardMenu from '../../components/DashboardMenu.jsx'
import Skeleton from '@mui/material/Skeleton';
import { Query } from 'appwrite'
import ThemeOption from '../../components/ThemeOption.jsx'
import LogoLoader from '../../components/LogoLoader';
import { styled } from '@mui/material/styles'

import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid2';
import { OutlinedCard } from '../../components/OutlinedCard.jsx';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Avatar from '@mui/material/Avatar';


const client = new Client()
    .setEndpoint(process.env.REACT_APP_ENDPOINT)
    .setProject(process.env.REACT_APP_PROJECT_ID);

const account = new Account(client);
const teams = new Teams(client);
const functions = new Functions(client);
const storage = new Storage(client);


const columns = [
    { field: 'id', headerName: 'ID', width: 50 },
    {
        field: 'avatar',
        headerName: 'Avatar',
        renderCell: (params) => {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Avatar src={params.value} />
                </Box>
            );
        },
        sortable: false,
        width: 75,
        editable: false,
    },
    {
        field: 'name',
        headerName: 'Username',
        width: 150,
        editable: true,
    },
    {
        field: 'email',
        headerName: 'Email',
        width: 250,
    },
    {
        field: 'permissions',
        headerName: 'Permissions',
        type: 'singleSelect',
        valueOptions: ['Viewer', 'Editor', 'Admin', 'Owner'],
        width: 150,
        editable: true,
        description: '',
    },
    // For info purposes
    // {
    //   field: 'fullName',
    //   headerName: 'Full name',
    //   description: 'This column has a value getter and is not sortable.',
    //   sortable: false,
    //   width: 160,
    //   valueGetter: (value, row) => `${row.firstName || ''} ${row.lastName || ''}`,
    // },
];

const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    '& .no-rows-primary': {
        fill: '#3D4751',
        ...theme.applyStyles('light', {
            fill: '#AEB8C2',
        }),
    },
    '& .no-rows-secondary': {
        fill: '#1D2126',
        ...theme.applyStyles('light', {
            fill: '#E8EAED',
        }),
    },
}));
  
function noRowsOverlay() {
    return (
        <StyledGridOverlay>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                width={96}
                viewBox="0 0 452 257"
                aria-hidden
                focusable="false"
            >
                <path
                    className="no-rows-primary"
                    d="M348 69c-46.392 0-84 37.608-84 84s37.608 84 84 84 84-37.608 84-84-37.608-84-84-84Zm-104 84c0-57.438 46.562-104 104-104s104 46.562 104 104-46.562 104-104 104-104-46.562-104-104Z"
                />
                <path
                    className="no-rows-primary"
                    d="M308.929 113.929c3.905-3.905 10.237-3.905 14.142 0l63.64 63.64c3.905 3.905 3.905 10.236 0 14.142-3.906 3.905-10.237 3.905-14.142 0l-63.64-63.64c-3.905-3.905-3.905-10.237 0-14.142Z"
                />
                <path
                    className="no-rows-primary"
                    d="M308.929 191.711c-3.905-3.906-3.905-10.237 0-14.142l63.64-63.64c3.905-3.905 10.236-3.905 14.142 0 3.905 3.905 3.905 10.237 0 14.142l-63.64 63.64c-3.905 3.905-10.237 3.905-14.142 0Z"
                />
                <path
                    className="no-rows-secondary"
                    d="M0 10C0 4.477 4.477 0 10 0h380c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 20 0 15.523 0 10ZM0 59c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 69 0 64.523 0 59ZM0 106c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 153c0-5.523 4.477-10 10-10h195.5c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 200c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 247c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10Z"
                />
            </svg>
            <Box sx={{ mt: 2 }}>No rows</Box>
        </StyledGridOverlay>
    );
}


export default function Admin() {
    const navigate = useNavigate()
    const [userSession, setUserSession] = useAtom(UserSession);
    const [teamsList, setTeamsList] = useAtom(TeamsList);
    const [rows, setRows] = useState([]);

    const [allTeams, setAllTeams] = useState([]);
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [email, setEmail] = useState('');

    const [isLoading, setIsLoading] = useState(true);
    const [showLoadingScreen, setShowLoadingScreen] = useState(false);

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    const handleChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedTeams(
            // On autofill we get a stringified value
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    useEffect(() => {
        if (userSession) {
            getTeams(userSession.$id).then((teams) => {
                setAllTeams(teams);
            })
        }
    }, [userSession]);

    // Display loading screen while the page loads
        useLayoutEffect(() => {
            const timeoutId = setTimeout(() => {
                setIsLoading(false);
            }, 0);
        
            return () => clearTimeout(timeoutId);
        }, []);
        
        if (isLoading) return <LogoLoader />;
    
    if (isLoading && showLoadingScreen) return <LogoLoader />;

    return (<DashboardMenu path="" user={userSession} needsMaster={true} page={userSession && (<div className="page-content">
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            {/* <Link underline="hover" color="inherit" href="/admin">
                Admin
            </Link>
            <Link underline="hover" color="inherit" href="/admin/teams">
                Teams
            </Link> */}
            <Typography variant='p' onClick={() => navigate('/admin')} sx={{ cursor: 'pointer' }}>Admin</Typography>
            <Typography variant='p' onClick={() => navigate('/admin/teams')} sx={{ cursor: 'pointer' }}>Teams</Typography>
        </Breadcrumbs>
        <Typography variant="h4" gutterBottom>
            All Teams
        </Typography>
        {/* <Typography variant="p" gutterBottom sx={{ color: 'text.secondary' }}>
            View all Takeoff Monkey Widgets users.
        </Typography> */}
        <Box sx={{ width: '100%', mt: 3 }}>
            <DataGrid
                key={allTeams}
                rows={rows}
                columns={columns}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 10,
                        },
                    },
                }}
                pageSizeOptions={[5]}
                checkboxSelection
                disableRowSelectionOnClick
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    },
                }}
                slots={{ noRowsOverlay: noRowsOverlay, toolbar: GridToolbar }}
                sx={{
                    '--DataGrid-overlayHeight': '300px',
                    // '& .MuiDataGrid-cell:hover': {
                    //     color: 'primary.main',
                    // },
                }}
            />
        </Box>
        <Popup
            openText="Create new team"
            openVariant="contained"
            header={"Invite new user"}
            sx={{ mt: 2 }}
            content={<>
                <Typography variant="p" gutterBottom>
                    Send invitation to:
                </Typography>
                <br/>
                <TextField
                    id="email-input"
                    label="Email"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                        '& .MuiInputBase-root': { height: '2.5em', overflow: 'hidden' },
                        fontSize: '0.5em',
                        width: '100%',
                        mb: 1.5
                    }}
                />
                <br/>
                <Typography variant="p" gutterBottom>
                    Add user to teams:
                </Typography>
                <br/>
                <FormControl sx={{ width: '40vw', minWidth: '300px' }}>
                    <InputLabel id="demo-multiple-chip-label">Select teams</InputLabel>
                    <Select
                        labelId="demo-multiple-chip-label"
                        multiple
                        value={selectedTeams}
                        onChange={handleChange}
                        input={<OutlinedInput id="select-multiple-chip" label="Select teams" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip avatar={<Avatar src={logo} alt={`${value} team logo`} />} key={value} label={value} />
                                ))}
                            </Box>
                        )}
                        MenuProps={MenuProps}
                    >
                        {allTeams.map((team) => (
                            <MenuItem
                                key={team.$id}
                                value={team.name}
                            >
                                {team.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </>}
            buttonSuccess={"Create user"}
            // FIXME: this needs to send an email to the user and walk them through creating an account instead
            // TODO: this needs to create a magic link and send it in the email
            successAction={() => createUser("Test User", email, "test1234")}
            successDisabled={!email || !selectedTeams.length}
            buttonCancel={"Cancel"}
        />
        <Button
            onClick={() => createTeam("Test Team", "KonurPapa", userSession.$id)}
            variant="contained"
            sx={{ mt: 2, ml: 2 }}
        >
            Do the thing
        </Button>
        <Button
            // onClick={() => deleteTeam("test-team", "KonurPapa")}
            // onClick={() => console.log(createBoard("test-team", "Main Board", false)) }
            onClick={() => {
                // console.log(createMember("test-team", userSession.$id, "KonurPapa", "owner"))
                console.log(getWidgetData("test-team", "KonurPapa", "New Thing", ["setting1"]))
                // console.log(addWidget("test-team", userSession.$id, 'the-widget', {
                //     'create': {
                //         'type': 'string',
                //         'array': true
                //     },
                //     'read': {
                //         'type': 'string',
                //         'array': true
                //     },
                //     'update': {
                //         'type': 'string',
                //         'array': true
                //     },
                //     'delete': {
                //         'type': 'string',
                //         'array': true
                //     },
                //     'access': {
                //         'type': 'string',
                //         'array': true
                //     }
                // }))
            }}
            // onClick={() => console.log(getUsers("KonurPapa"))}
            variant="contained"
            sx={{ mt: 2, ml: 2 }}
        >
            Do the other thing
        </Button>
    </div>)} />)
}